import {
  useCallback,
  useLayoutEffect,
  useState,
  useRef,
  MutableRefObject,
  RefObject,
} from 'react';
import { isEqual, throttle } from 'lodash';
import { type IMaterialTableProps } from '../../components';

interface IUseTableDimensionsProps<RowData extends object> {
  skipPagination: boolean;
  tableRef: MutableRefObject<{ tableContainerDiv: RefObject<HTMLDivElement> } | null>;
  bottomMargin?: number;
  data: IMaterialTableProps<RowData>['data'];
}

export interface ITableDimensions {
  tableWidth: number;
  tableHeight: number | typeof NONE;
}

interface IUseTableDimensionsReturn extends ITableDimensions {
  paginationRef: MutableRefObject<HTMLTableCellElement | null>;
}

const NONE = 'none';

const AUTHORIZED_LAYOUT_BOTTOM_PADDING = 16;

const useTableDimensions = <RowData extends object>(
  params: IUseTableDimensionsProps<RowData>,
): IUseTableDimensionsReturn => {
  const {
    skipPagination,
    tableRef,
    bottomMargin = AUTHORIZED_LAYOUT_BOTTOM_PADDING,
    data,
  } = params;

  const paginationRef = useRef<HTMLTableCellElement>(null);

  const [tableDimensions, setTableDimensions] = useState<ITableDimensions>({
    tableWidth: 0, tableHeight: NONE,
  });

  const calculateTableDimensions = useCallback(() => {
    const {
      width: tableWidth = 0,
      top = 0, // space above the table
    } = tableRef.current?.tableContainerDiv.current?.offsetParent?.getBoundingClientRect() ?? {};
    let paginationHeight = 0;
    if (!skipPagination && paginationRef.current) {
      paginationHeight = paginationRef.current.getBoundingClientRect().height;
    }
    const tableHeight = window.innerHeight - top - paginationHeight - bottomMargin;
    return { tableWidth, tableHeight };
  }, [tableRef, skipPagination, bottomMargin]);

  const changeTableDimensions = useCallback(() => {
    const { tableWidth, tableHeight } = calculateTableDimensions();

    const newTableDimensions = {
      tableWidth: Math.floor(tableWidth),
      tableHeight,
    } as ITableDimensions;

    setTableDimensions((prevTableDimensions) => {
      if (isEqual(prevTableDimensions, newTableDimensions)) {
        return prevTableDimensions;
      }
      return newTableDimensions;
    });
  }, [calculateTableDimensions]);

  const paginationElement = paginationRef.current;

  useLayoutEffect(() => {
    changeTableDimensions();
    const throttledChangeTableDimensions = throttle(changeTableDimensions, 200);
    window.addEventListener('resize', throttledChangeTableDimensions);
    return () => {
      window.removeEventListener('resize', throttledChangeTableDimensions);
    };
  }, [
    /*
      this Effect should depend on the data and pagination element so table height is
      calculated correctly
    */
    data,
    paginationElement,
    changeTableDimensions,
  ]);

  return {
    paginationRef,
    ...tableDimensions,
  };
};

export default useTableDimensions;
